import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PenLine, Loader2, RotateCcw, Check } from "lucide-react";
import { toast } from "sonner";

export default function ShipmentSignatureDialog({ open, onOpenChange, order, onSaved }) {
  const sigRef = useRef(null);
  const [signerName, setSignerName] = useState("");
  const [signerRole, setSignerRole] = useState("musteri");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSignerName("");
      setSignerRole("musteri");
      setNotes("");
      setTimeout(() => sigRef.current?.clear(), 100);
    }
  }, [open]);

  const clearSig = () => sigRef.current?.clear();

  const save = async () => {
    if (!signerName.trim()) {
      toast.error("İmzalayan kişinin adı gerekli");
      return;
    }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error("Lütfen imza atın");
      return;
    }
    const dataUrl = sigRef.current.getCanvas().toDataURL("image/png");
    setSaving(true);
    try {
      await api.post("/shipments/signatures", {
        order_id: order.id,
        signer_name: signerName.trim(),
        signer_role: signerRole,
        signature_data_url: dataUrl,
        notes,
      });
      toast.success("İmza kaydedildi · Sevkiyat tamamlandı");
      onOpenChange?.(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2">
            <PenLine className="w-5 h-5 text-blue-500" />
            Sevkiyat İmzası
          </DialogTitle>
          {order && (
            <div className="text-xs text-slate-400 mt-1">
              <span className="font-mono text-blue-500">{order.order_no}</span> · {order.customer_name}
            </div>
          )}
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2 col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">İmzalayan Adı *</Label>
              <Input
                data-testid="signer-name-input"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Ad Soyad"
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Rol</Label>
              <Select value={signerRole} onValueChange={setSignerRole}>
                <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                  <SelectItem value="musteri">Müşteri</SelectItem>
                  <SelectItem value="alici">Alıcı</SelectItem>
                  <SelectItem value="kurye">Kurye</SelectItem>
                  <SelectItem value="depo">Depo Sorumlusu</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Not</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Opsiyonel"
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">İmza</Label>
              <button
                type="button"
                onClick={clearSig}
                data-testid="clear-sig-btn"
                className="text-[10px] uppercase tracking-widest font-mono text-slate-500 hover:text-blue-500 flex items-center gap-1"
              >
                <RotateCcw className="w-3 h-3" /> Temizle
              </button>
            </div>
            <div className="bg-white rounded-sm border border-slate-700 relative overflow-hidden" data-testid="signature-pad">
              <SignatureCanvas
                ref={sigRef}
                penColor="#0f172a"
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: "w-full h-[200px] touch-none",
                }}
              />
              <div className="absolute bottom-2 left-3 text-[10px] font-mono uppercase tracking-widest text-slate-400 pointer-events-none">
                Bu alana imzanızı atın →
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm"
          >
            İptal
          </Button>
          <Button
            data-testid="save-signature-btn"
            onClick={save}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> Sevkiyatı Kapat</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
